// src/services/timeClockService.ts
// Employee time-clock (打卡) API wrapper. Backend routes live under
// /api/employee/punch (see backend/src/routes/employee.routes.js).
import api from './api';

export type PunchType = 'in' | 'out';

// Most recent punch for the selected person (GET /punch/status), or null.
export interface PunchStatus {
  punch_type: PunchType;
  punched_at: string; // ISO timestamp (UTC instant)
  note: string | null;
  // 'in' too old to close (backend MAX_SHIFT_HOURS): not on shift any more —
  // the next punch is 上班, and the old one stays a 未打下班卡 hole for payroll.
  stale?: boolean;
}

// Row returned by POST /punch.
export interface PunchRecord {
  id: number;
  user_id: number;
  punch_type: PunchType;
  punched_at: string;
  ip_address: string | null;
  note: string | null;
  punched_by: number | null; // logged-in account that pressed the button
  recorded_at: string;       // real wall-clock moment (vs adjusted punched_at)
}

// Args for POST /punch. user_id lets a shared warehouse PC punch on behalf of
// another staff member; punched_at/note come from the 下班打卡 dialog.
export interface PunchArgs {
  type: PunchType;
  userId?: number;
  punchedAt?: Date;
  note?: string;
}

// Staff selectable in the 打卡人員 picker (GET /api/auth/users).
export interface StaffOption {
  id: number;
  name: string;
  email: string;
}

// One paired (or unmatched) attendance row for a single Taipei day.
export interface MonthlyDayRow {
  date: string;            // YYYY-MM-DD (Taipei)
  in_time: string | null;  // HH:mm (Taipei) or null
  out_time: string | null; // HH:mm (Taipei) or null
  hours: number;           // 0 for open/anomaly rows
  open: boolean;           // 'in' with no matching 'out' (未打下班卡)
  anomaly: boolean;        // 'out' with no preceding 'in'
  note: string | null;     // 備註 from either punch of the pair
  out_date?: string | null; // set when the shift ended on a later day (跨日)
  adjusted_minutes?: number | null; // punch time hand-adjusted by ≥10 min
}

export interface PayrollLine {
  label: string;  // full detail incl. formula (owner view)
  amount: number;
  name?: string;  // employee-facing item name (薪資單)
  note?: string;  // employee-facing qualifier, e.g. "141.41 小時"
}

export interface PayrollAlert {
  level: 'error' | 'warning' | 'info';
  date: string; // YYYY-MM-DD, '' for person-level alerts
  message: string;
}

// One day of a 月薪 employee's attendance (feeds the 出勤明細 sheet).
export interface PayrollDay {
  date: string;
  kind: 'weekday' | 'rest_day' | 'holiday';
  on_site: number;
  break: number;
  work: number;
  overtime: number;
  short: number;        // 早退 hours deducted
  short_amount: number;
}

// Month-to-date pay from backend payroll.service.js.
export interface Payroll {
  configured: boolean;
  type: 'hourly' | 'monthly';
  hours: Record<string, number>;
  lines: PayrollLine[];
  deductions: PayrollLine[];
  gross: number;
  net: number;
  alerts: PayrollAlert[];
  hourly_rate?: number;            // 月薪 ÷ 240 (monthly only)
  weekday_overtime_paid?: boolean; // false = 平日加班 settled outside payroll
  early_leave_offset?: boolean;    // 早退 offset by weekday time past 8h before deducting
  days?: PayrollDay[];             // monthly only
}

export interface MonthlyUser {
  user_id: number;
  employee_name: string;
  total_hours: number;
  rows: MonthlyDayRow[];
  payroll?: Payroll;
}

export interface MonthlyResponse {
  month: string;
  users: MonthlyUser[];
}

class TimeClockService {
  // Record a punch. Throws on 409 (duplicate same-type punch) and on 400
  // (adjusted time out of range / earlier than the punch it closes).
  async punch({ type, userId, punchedAt, note }: PunchArgs): Promise<PunchRecord> {
    const res = await api.post('/api/employee/punch', {
      type,
      user_id: userId,
      punched_at: punchedAt ? punchedAt.toISOString() : undefined,
      note: note || undefined,
    });
    return res.data;
  }

  async getStatus(userId?: number): Promise<PunchStatus | null> {
    const res = await api.get('/api/employee/punch/status', {
      params: userId ? { user_id: userId } : undefined,
    });
    return res.data;
  }

  // Staff list for the warehouse 打卡人員 picker — same endpoint the task board
  // uses, admin-only. 門市人員 get 403 here; they use getCounterStaff instead.
  async getStaff(): Promise<StaffOption[]> {
    const res = await api.get('/api/auth/users');
    return res.data;
  }

  // 忠泰門市 punch roster. Separate endpoint because /api/auth/users is
  // admin-only and lists the warehouse, which 門市人員 have no business seeing.
  async getCounterStaff(): Promise<StaffOption[]> {
    const res = await api.get('/api/employee/punch/staff');
    return res.data;
  }

  // Owner-only on the backend (ownerAuth). month = 'YYYY-MM'. This is the ONLY
  // way to read worked hours — the 門市 tablet shows none (see the punch page).
  async getMonthly(month: string): Promise<MonthlyResponse> {
    const res = await api.get('/api/employee/punch/monthly', { params: { month } });
    return res.data;
  }
}

export default new TimeClockService();
